export type PersonnelAssignment = {
  userId: string;
  roleId: string;
  primaryContact?: boolean;
};

export const assignmentKey = ({ userId, roleId }: PersonnelAssignment) =>
  `${userId}:${roleId}`;

/** Users whose selected role set changed while the editor was open. */
export function changedPersonnelUserIds(
  initial: PersonnelAssignment[],
  edited: PersonnelAssignment[],
) {
  const initialByUser = new Map<string, string[]>();
  const editedByUser = new Map<string, string[]>();
  for (const item of initial)
    initialByUser.set(item.userId, [
      ...(initialByUser.get(item.userId) ?? []),
      item.roleId,
    ]);
  for (const item of edited)
    editedByUser.set(item.userId, [
      ...(editedByUser.get(item.userId) ?? []),
      item.roleId,
    ]);
  return new Set(
    [...new Set([...initialByUser.keys(), ...editedByUser.keys()])].filter(
      (userId) =>
        [...(initialByUser.get(userId) ?? [])].sort().join() !==
        [...(editedByUser.get(userId) ?? [])].sort().join(),
    ),
  );
}

/** Apply staged person edits to a fresh matter snapshot after team assignment. */
export function mergePersonnelAssignments({
  initial,
  edited,
  current,
  removedUserIds,
  primaryUserId,
}: {
  initial: PersonnelAssignment[];
  edited: PersonnelAssignment[];
  current: PersonnelAssignment[];
  removedUserIds: string[];
  primaryUserId: string | null;
}) {
  const removedUsers = new Set(removedUserIds);
  const changedUsers = changedPersonnelUserIds(initial, edited);
  const merged = [
    ...new Map(
      [
        ...current.filter(
          (item) =>
            !removedUsers.has(item.userId) && !changedUsers.has(item.userId),
        ),
        ...edited,
      ].map((item) => [assignmentKey(item), item]),
    ).values(),
  ];
  const selectedPrimary =
    merged.find(
      (item) => item.userId === primaryUserId && item.primaryContact,
    ) ?? merged.find((item) => item.userId === primaryUserId);
  return merged.map((item) => ({
    userId: item.userId,
    roleId: item.roleId,
    primaryContact: item === selectedPrimary,
  }));
}
